import { cn } from '@/lib/utils'
import { rowLabel } from '@/lib/storageTree'
import { cellSizeFor, previewRows, showsCode, type CellSize } from '@/lib/slotMetrics'
import type { GridCellLabel } from '@/lib/slotLabels'
import type { SlotLabelScheme } from '@/types/storage'

/**
 * The matrix a declaration will produce, drawn as a matrix.
 *
 * A layout IS a grid, so describing one as a wrapped list of names hides the
 * only thing that matters — 2×5 and 5×2 mint identical name lists and are
 * completely different shelves. This renders the real arrangement, with the
 * same row/column gutters and cell geometry the live slot map uses, so what
 * you approve is what you later see.
 */

const SIZE_STYLES: Record<CellSize, { cell: string; min: string; text: string }> = {
  lg: { cell: 'h-12', min: '3rem', text: 'text-xs' },
  md: { cell: 'h-9', min: '2.25rem', text: 'text-[11px]' },
  sm: { cell: 'h-6', min: '1.5rem', text: 'text-[9px]' },
}

export function MatrixPreview({
  rows,
  columns,
  cells,
  scheme,
  className,
}: {
  rows: number
  columns: number
  /** In reading order, as `gridCellLabels` returns them. */
  cells: GridCellLabel[]
  scheme: SlotLabelScheme
  className?: string
}) {
  const extent = previewRows(rows, columns)
  if (extent.rowsShown === 0 || cells.length === 0) return null

  const size = cellSizeFor(rows * columns)
  const geom = SIZE_STYLES[size]
  const template = `repeat(${columns}, minmax(${geom.min}, 1fr))`

  const byCoord = new Map(cells.map((c) => [`${c.gridRow}:${c.gridColumn}`, c]))

  return (
    <div className={cn('overflow-x-auto pb-1', className)}>
      <div className='inline-block min-w-0'>
        <div className='flex gap-2'>
          <div className='w-5 shrink-0' aria-hidden />
          <div className='grid gap-1' style={{ gridTemplateColumns: template }}>
            {Array.from({ length: columns }, (_, c) => (
              <div key={c} className='text-center text-[10px] text-muted-foreground'>
                {c + 1}
              </div>
            ))}
          </div>
        </div>

        {Array.from({ length: extent.rowsShown }, (_, r) => (
          <div key={r} className='mt-1 flex items-center gap-2'>
            <div className='w-5 shrink-0 text-right text-[10px] text-muted-foreground'>
              {/* SEQUENTIAL names carry no row letter, so the gutter shows the
                  row number rather than implying an A/B/C the codes don't use. */}
              {scheme === 'SEQUENTIAL' ? r + 1 : rowLabel(r)}
            </div>
            <div className='grid gap-1' style={{ gridTemplateColumns: template }}>
              {Array.from({ length: columns }, (_, c) => {
                const cell = byCoord.get(`${r + 1}:${c + 1}`)
                return (
                  <div
                    key={c}
                    title={cell?.code}
                    className={cn(
                      'flex items-center justify-center truncate rounded border border-dashed border-primary/30 bg-primary/5 px-1 font-medium text-primary',
                      geom.cell,
                      geom.text,
                    )}
                  >
                    {cell && showsCode(size) ? cell.code : ''}
                  </div>
                )
              })}
            </div>
          </div>
        ))}

        {extent.truncated && (
          <p className='mt-1.5 pl-7 text-[11px] text-muted-foreground'>
            + {extent.hiddenRows} more {extent.hiddenRows === 1 ? 'row' : 'rows'}, all created the
            same way
          </p>
        )}
      </div>
    </div>
  )
}
